/* V192 — Google Search Console Service Account Ownership Setup Admin UI
 * Step-by-step localized guide + 1-click verify + dry-run test publish.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminGscSetupTab() {
  const [info, setInfo] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [lang, setLang] = useState("sk");
  const [testUrl, setTestUrl] = useState("https://toprankpulse.com/");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [testing, setTesting] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const token = () => localStorage.getItem("admin_token") || "";

  const loadInfo = async (langOverride) => {
    setLoading(true); setErr("");
    try {
      const r = await axios.get(`${API}/gsc-setup/info?lang=${langOverride || lang}`);
      setInfo(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "load_failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadInfo(); /* eslint-disable-next-line */ }, []);

  const changeLang = (next) => { setLang(next); loadInfo(next); };

  const verify = async () => {
    if (!token()) { setErr("Admin token chýba."); return; }
    setVerifying(true); setErr(""); setVerifyResult(null);
    try {
      const r = await axios.get(`${API}/gsc-setup/verify`,
        { headers: { "X-Admin-Token": token() } });
      setVerifyResult(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "verify_failed");
    } finally { setVerifying(false); }
  };

  const test = async () => {
    if (!token()) { setErr("Admin token chýba."); return; }
    setTesting(true); setErr(""); setTestResult(null);
    try {
      const r = await axios.post(`${API}/gsc-setup/test-publish`,
        { url: testUrl },
        { headers: { "X-Admin-Token": token() } });
      setTestResult(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "test_failed");
    } finally { setTesting(false); }
  };

  const copyEmail = () => {
    if (!info?.service_account_email) return;
    navigator.clipboard.writeText(info.service_account_email).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const LANGS = [
    { key: "sk", label: "🇸🇰 Slovenčina" },
    { key: "en", label: "🇬🇧 English" },
    { key: "cs", label: "🇨🇿 Čeština" },
    { key: "de", label: "🇩🇪 Deutsch" },
  ];

  return (
    <div className="p-4 space-y-5" data-testid="gsc-setup-tab">
      <header>
        <h2 className="text-xl font-bold flex items-center gap-2">
          🔍 Google Search Console Setup
          <span className="text-[10px] tracking-widest bg-blue-500 text-black px-2 py-0.5 font-black uppercase">V192</span>
        </h2>
        <p className="text-sm text-zinc-400 mt-1">
          Pridaj náš service account ako <strong className="text-zinc-200">Owner</strong> v Search Console — odomkneš tým instant Google indexing pre programmatic SEO stránky.
        </p>
      </header>

      {loading && <div className="text-zinc-500 text-sm">Načítavam…</div>}
      {err && (
        <div className="bg-rose-950/40 border border-rose-700 text-rose-300 p-3 text-sm" data-testid="gsc-setup-error">
          {err}
        </div>
      )}

      {info && (
        <>
          {/* Service Account email box */}
          <section className="border-2 border-amber-700 bg-amber-950/30 p-5" data-testid="gsc-sa-email-box">
            <div className="text-[10px] uppercase tracking-widest text-amber-400 font-black mb-1">⚠️ Service Account EMAIL — túto adresu pridáš v kroku 6</div>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-sm md:text-base font-mono bg-black text-amber-200 px-3 py-2 break-all flex-1 border border-amber-800"
                    data-testid="gsc-sa-email">
                {info.service_account_email || "— SA JSON missing in .env —"}
              </code>
              {info.service_account_email && (
                <button onClick={copyEmail}
                        data-testid="gsc-copy-email"
                        className="text-xs px-3 py-2 bg-amber-500 hover:bg-amber-400 text-black font-black uppercase tracking-wider">
                  {copied ? "✓ Skopírované" : "📋 Kopírovať"}
                </button>
              )}
            </div>
            <div className="flex items-center gap-3 mt-3 text-[11px]">
              <span className={`px-2 py-0.5 font-black uppercase tracking-wider ${info.sa_configured
                ? "bg-emerald-700 text-white" : "bg-rose-700 text-white"}`}>
                SA JSON: {info.sa_configured ? "✅ Configured" : "❌ Missing"}
              </span>
              <span className={`px-2 py-0.5 font-black uppercase tracking-wider ${info.indexing_enabled_in_env
                ? "bg-emerald-700 text-white" : "bg-amber-700 text-white"}`}>
                INDEXING_ENABLED: {info.indexing_enabled_in_env ? "✅ true" : "⚠ false"}
              </span>
            </div>
          </section>

          {/* Language toggle */}
          <section className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-zinc-500 uppercase tracking-widest font-black">Jazyk návodu:</span>
            {LANGS.map(l => (
              <button key={l.key}
                      onClick={() => changeLang(l.key)}
                      data-testid={`gsc-lang-${l.key}`}
                      className={`text-xs px-3 py-1.5 border ${lang === l.key
                        ? "bg-blue-600 border-blue-500 text-white font-black"
                        : "bg-zinc-900 border-zinc-700 text-zinc-300 hover:bg-zinc-800"}`}>
                {l.label}
              </button>
            ))}
          </section>

          {/* Step-by-step guide */}
          <section className="border border-zinc-800 bg-zinc-950 p-4" data-testid="gsc-setup-steps">
            <h3 className="text-sm uppercase tracking-widest text-zinc-500 font-black mb-3">📋 Návod krok-po-kroku</h3>
            <ol className="space-y-2">
              {(info.steps || []).map((step, i) => (
                <li key={i} className="flex gap-3 items-start" data-testid={`gsc-step-${i + 1}`}>
                  <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white text-xs font-black flex items-center justify-center rounded-sm">
                    {i + 1}
                  </span>
                  <span className="text-sm text-zinc-200 leading-relaxed pt-1">{step}</span>
                </li>
              ))}
            </ol>
            {info.docs_url && (
              <a href={info.docs_url} target="_blank" rel="noopener noreferrer"
                 className="inline-block mt-4 text-[11px] text-blue-400 hover:underline">
                📖 Google Indexing API prerequisites docs ↗
              </a>
            )}
          </section>

          {/* Verify */}
          <section className="border-2 border-emerald-700 bg-emerald-950/20 p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="text-sm font-black uppercase tracking-widest text-emerald-300">✅ Krok 8 — Overiť konfiguráciu</h3>
                <p className="text-xs text-zinc-400 mt-1">
                  Skúsi prečítať zoznam sajtov z Search Console pomocou service accountu. Ak vidí aspoň 1 doménu — Owner role je nastavený správne.
                </p>
              </div>
              <button onClick={verify}
                      disabled={verifying}
                      data-testid="gsc-verify-btn"
                      className="text-sm px-5 py-2 bg-emerald-500 hover:bg-emerald-400 text-black font-black uppercase tracking-wider disabled:opacity-50">
                {verifying ? "Overujem…" : "🔎 Overiť"}
              </button>
            </div>

            {verifyResult && (
              <div className="border border-zinc-800 bg-black p-3 text-xs font-mono" data-testid="gsc-verify-result">
                <div className={`text-sm font-black mb-2 ${verifyResult.ok ? "text-emerald-300" : "text-rose-300"}`}>
                  {verifyResult.ok ? "✅ Ownership je správne nastavené!" : "❌ Ownership ešte nie je hotové"}
                </div>
                {verifyResult.sites_total !== undefined && (
                  <div className="text-zinc-300 mb-1">
                    Total sites: <span className="text-amber-300">{verifyResult.sites_total}</span>
                    {" · "}
                    With ownership: <span className="text-emerald-300">{verifyResult.sites_with_ownership || 0}</span>
                  </div>
                )}
                {verifyResult.owned_sites?.length > 0 && (
                  <div className="mt-2">
                    <div className="text-zinc-500 mb-1">Owned:</div>
                    {verifyResult.owned_sites.map((s, i) => (
                      <div key={i} className="text-emerald-200">• {s.url} ({s.permission})</div>
                    ))}
                  </div>
                )}
                {verifyResult.stage && (
                  <div className="text-rose-400 mt-1">Stage: {verifyResult.stage}</div>
                )}
                {verifyResult.error && (
                  <div className="text-rose-400 mt-1 break-all">Error: {verifyResult.error}</div>
                )}
                {verifyResult.hint && (
                  <div className="text-amber-300 mt-2">💡 {verifyResult.hint}</div>
                )}
              </div>
            )}
          </section>

          {/* Test publish */}
          <section className="border border-zinc-800 bg-zinc-950 p-4 space-y-3">
            <h3 className="text-sm font-black uppercase tracking-widest text-zinc-300">🧪 Dry-run: test publish</h3>
            <p className="text-xs text-zinc-400">
              Pošle jednorázový indexing ping pre URL. Ak ownership je hotové, dostaneš <code className="bg-zinc-800 px-1">notifyTime</code> v response. Ak nie, uvidíš presný error od Google.
            </p>
            <div className="flex gap-2 flex-wrap">
              <input type="url"
                     value={testUrl}
                     onChange={(e) => setTestUrl(e.target.value)}
                     placeholder="https://toprankpulse.com/audit/..."
                     data-testid="gsc-test-url-input"
                     className="flex-1 min-w-[280px] bg-black border border-zinc-700 text-zinc-200 px-3 py-2 text-sm font-mono" />
              <button onClick={test}
                      disabled={testing || !testUrl}
                      data-testid="gsc-test-publish-btn"
                      className="text-sm px-5 py-2 bg-blue-500 hover:bg-blue-400 text-black font-black uppercase tracking-wider disabled:opacity-50">
                {testing ? "Pingujem…" : "🚀 Test ping"}
              </button>
            </div>

            {testResult && (
              <pre className="border border-zinc-800 bg-black p-3 text-xs text-zinc-300 overflow-auto max-h-64" data-testid="gsc-test-result">
                {JSON.stringify(testResult, null, 2)}
              </pre>
            )}
          </section>
        </>
      )}
    </div>
  );
}
